import { editableNumber, parseNumberInput } from '../charts/numberFormat.js'
import { display, place } from './objectLayout.js'

/**
 * RED-317 PR 3d-B — an embedded Excel object's cells as the data sheet
 * (StudioDataSheet.vue) edits them, and the override a commit stores on the
 * rawRun: `objectEdit = { v: 1, cells: { E7: 712345, B10: '55-69', J9: null } }`,
 * only the cells whose value differs from the workbook's (null empties one).
 * Pure, so it unit-tests in node.
 *
 * What a cell takes: a number cell an Estonian number, a text cell any
 * text up to MAX_CELL_TEXT characters, an empty cell a number when it reads
 * as one, else text. A number must fit its column (Excel would show ####)
 * and every character must be one the preview can measure; what does not
 * pass stays in its cell, flagged (`invalid`), never applied and never
 * cleared.
 */

export const OBJECT_EDIT_VERSION = 1

/** The longest text a cell takes, in characters (ExcelObjectWriter::MAX_TEXT: the server refuses more). */
export const MAX_CELL_TEXT = 1000

/** A cell's value now: the override's, else the workbook's. */
export function valueOf(model, edit, ref) {
  const cells = edit?.cells ?? {}
  return Object.hasOwn(cells, ref) ? cells[ref] : (model.cells[ref]?.value ?? null)
}

/** The text a cell shows with its value now. */
export function cellText(model, edit, ref) {
  const cell = model.cells[ref]
  const value = valueOf(model, edit, ref)
  return display(value, typeof value === 'string' ? null : cell.format, model.group)
}

/** What a typed text means for a cell: { ok, value } or { ok: false, reason: 'number' | 'text' | 'long' }. */
export function parseCellInput(cell, text) {
  const raw = String(text ?? '')
  if (raw.trim() === '') return { ok: true, value: null }
  const chars = [...raw]
  if (chars.length > MAX_CELL_TEXT) return { ok: false, reason: 'long' }
  if (chars.some((char) => char.charCodeAt(0) < 0x20 || char.charCodeAt(0) === 0x7f)) return { ok: false, reason: 'text' }
  if (cell.kind === 'text') return { ok: true, value: raw }
  const number = parseNumberInput(raw)
  if (cell.kind === 'number') return number.ok && number.value !== null ? { ok: true, value: number.value } : { ok: false, reason: 'number' }
  return number.ok && number.value !== null ? { ok: true, value: number.value } : { ok: true, value: raw }
}

/** Whether a value can be drawn in the cell: { ok } or { ok: false, reason: 'char' | 'fit' }. */
export function checkValue(model, fonts, ref, value) {
  const cell = model.cells[ref]
  if (value === null) return { ok: true }
  const text = display(value, typeof value === 'string' ? null : cell.format, model.group)
  if (text === '') return { ok: true }
  const font = model.fonts[cell.font]
  const placed = font ? place(fonts, cell, [{ text, font }], model.pads, typeof value === 'number') : null
  if (!placed) return { ok: false, reason: 'char' }
  const [x0, , x1] = cell.box
  if (typeof value === 'number' && placed[0].width > x1 - x0 - model.pads.left - model.pads.right) return { ok: false, reason: 'fit' }
  return { ok: true }
}

/** The override with one cell set (dropped again when it equals the workbook's value); null when nothing differs. */
export function setValue(model, edit, ref, value) {
  const cells = { ...(edit?.cells ?? {}) }
  const base = model.cells[ref]?.value ?? null
  if (value === base) delete cells[ref]
  else cells[ref] = value
  return Object.keys(cells).length ? { v: OBJECT_EDIT_VERSION, cells } : null
}

export const invalidKey = (ref) => ref

/**
 * Typed texts applied to the override: what parses and fits is set, what
 * does not comes back in `invalid` (ref → { text, reason }).
 * → { edit, invalid, changed }
 */
export function applyCellInputs(model, fonts, edit, inputs, invalid = {}) {
  let next = edit ?? null
  const flagged = { ...invalid }
  for (const { ref, text } of inputs) {
    const cell = model.cells[ref]
    if (!cell || cell.readonly) continue
    const parsed = parseCellInput(cell, text)
    const checked = parsed.ok ? checkValue(model, fonts, ref, parsed.value) : parsed
    if (!checked.ok) {
      flagged[ref] = { text: String(text), reason: checked.reason }
      continue
    }
    delete flagged[ref]
    next = setValue(model, next, ref, parsed.value)
  }
  return { edit: next, invalid: flagged, changed: JSON.stringify(next) !== JSON.stringify(edit ?? null) }
}

/** The sheet's columns: one per column of the object's range, headed by its letter. */
export function sheetColumns(model) {
  return model.cols.map((column) => ({ key: column.col, header: column.col, headerEditable: false, kind: 'text', leading: false, label: column.col }))
}

/** The A1 reference of the sheet cell at (row, col). */
export function refAt(model, row, col) {
  const column = model.cols[col]
  const line = model.rows[row]
  return column && line ? `${column.col}${line.row}` : null
}

/** The sheet's rows: the range's rows, headed by their row numbers. */
export function sheetRows(model, edit, invalid = {}, messages = {}) {
  return model.rows.map((line) => ({
    key: `r${line.row}`,
    label: String(line.row),
    header: String(line.row),
    cells: model.cols.map((column) => {
      const ref = `${column.col}${line.row}`
      const cell = model.cells[ref]
      if (!cell || cell.covered) return { display: '', edit: '', readonly: true, title: messages.merged }
      const value = valueOf(model, edit, ref)
      const flagged = invalid[ref]
      return {
        display: cellText(model, edit, ref),
        edit: typeof value === 'number' ? editableNumber(value) : (value ?? ''),
        numeric: typeof value === 'number' || (value === null && cell.kind === 'number'),
        readonly: Boolean(cell.readonly),
        title: cell.readonly ? (messages[cell.readonly] ?? messages.readonly) : undefined,
        ...(flagged ? { invalid: flagged.text, invalidMessage: messages[flagged.reason] } : {}),
      }
    }),
  }))
}

/** The sheet cell (row, col) under a point of the preview, in its own pixels; null outside the cells. */
export function cellAt(model, x, y) {
  const col = model.cols.findIndex((column) => x >= column.x0 && x < column.x1)
  const row = model.rows.findIndex((line) => y >= line.y0 && y < line.y1)
  if (col < 0 || row < 0) return null
  const ref = refAt(model, row, col)
  const anchor = model.cells[ref]?.covered
  if (!anchor) return { row, col }
  const cell = model.cells[anchor]
  return { row: cell.row, col: cell.col }
}
